import { minutesToSlotHours } from "@/shared/lib/private-lesson-slot-duration";
import { applyGroupPackageSellDiscount } from "./group-package-pricing";
import type { OffSiteDistance } from "./off-site-distance";
import { normalizeOffSiteDistance } from "./off-site-distance";

export type PrivateSharedTier = {
  minPeople: number;
  maxPeople: number;
  pricePerHour: number;
  pricePerHourOffSiteNear?: number | null;
  pricePerHourOffSiteFar?: number | null;
  extraPersonPrice?: number | null;
  sortOrder?: number;
};

export type ResolvePrivateSharedTierRateResult = {
  ratePerHour: number;
  tier: PrivateSharedTier | null;
  extraPeople: number;
  topTier: PrivateSharedTier;
};

export type PrivateSharedSellSession = {
  durationMinutes: number;
  locationKind?: "studio" | "off_site";
  offSiteDistance?: OffSiteDistance | null;
};

export type PrivateSharedHourSplit = {
  studioHours: number;
  nearHours: number;
  farHours: number;
};

export function sortPrivateSharedTiers(
  tiers: PrivateSharedTier[],
): PrivateSharedTier[] {
  return [...tiers].sort((a, b) => {
    const orderDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (orderDiff !== 0) return orderDiff;
    return a.minPeople - b.minPeople;
  });
}

export function resolvePrivateSharedTierRate(
  tiers: PrivateSharedTier[],
  headcount: number,
): ResolvePrivateSharedTierRateResult {
  if (tiers.length === 0) {
    throw new Error("Template has no pricing tiers");
  }
  if (headcount < 1) {
    throw new Error("Headcount must be at least 1");
  }

  const sorted = sortPrivateSharedTiers(tiers);
  const topTier = sorted[sorted.length - 1]!;

  const matched = sorted.find(
    (t) => headcount >= t.minPeople && headcount <= t.maxPeople,
  );
  if (matched) {
    return {
      ratePerHour: matched.pricePerHour,
      tier: matched,
      extraPeople: 0,
      topTier,
    };
  }

  if (headcount > topTier.maxPeople) {
    if (topTier.extraPersonPrice == null || topTier.extraPersonPrice <= 0) {
      throw new Error(
        `Group size ${headcount} exceeds maximum ${topTier.maxPeople}`,
      );
    }
    const extraPeople = headcount - topTier.maxPeople;
    return {
      ratePerHour:
        topTier.pricePerHour + extraPeople * topTier.extraPersonPrice,
      tier: null,
      extraPeople,
      topTier,
    };
  }

  throw new Error(`Group size ${headcount} is below minimum tier`);
}

function offSiteBaseRateForTier(
  tier: PrivateSharedTier,
  offSiteDistance: OffSiteDistance,
): number {
  if (offSiteDistance === "far") {
    return tier.pricePerHourOffSiteFar ?? tier.pricePerHour;
  }
  return tier.pricePerHourOffSiteNear ?? tier.pricePerHour;
}

export function resolvePrivateSharedSessionRate(
  tiers: PrivateSharedTier[],
  headcount: number,
  locationKind: "studio" | "off_site",
  offSiteDistance: OffSiteDistance | null,
): number {
  const resolved = resolvePrivateSharedTierRate(tiers, headcount);

  if (locationKind === "studio") {
    return resolved.ratePerHour;
  }

  const baseTier = resolved.tier ?? resolved.topTier;
  const baseOffSite = offSiteBaseRateForTier(
    baseTier,
    offSiteDistance ?? "near",
  );

  if (resolved.extraPeople > 0 && resolved.topTier.extraPersonPrice != null) {
    return baseOffSite + resolved.extraPeople * resolved.topTier.extraPersonPrice;
  }

  return baseOffSite;
}

export function computePrivateSharedOriginalPrice(
  ratePerHour: number,
  purchasedHours: number,
): number {
  if (purchasedHours < 1) return 0;
  return ratePerHour * purchasedHours;
}

function applySellDiscount(
  originalPrice: number,
  discountType?: "percentage" | "fixed",
  discountValue?: number,
) {
  return applyGroupPackageSellDiscount(originalPrice, discountType, discountValue);
}

export function computePrivateSharedSellPriceFromSessions(
  tiers: PrivateSharedTier[],
  headcount: number,
  sessions: PrivateSharedSellSession[],
  discountType?: "percentage" | "fixed",
  discountValue?: number,
) {
  const { tier, extraPeople, topTier } = resolvePrivateSharedTierRate(
    tiers,
    headcount,
  );

  const originalPrice = sessions.reduce((sum, session) => {
    const locationKind = session.locationKind ?? "studio";
    const offSiteDistance = normalizeOffSiteDistance(
      locationKind,
      session.offSiteDistance,
    );
    const hours = minutesToSlotHours(session.durationMinutes);
    const rate = resolvePrivateSharedSessionRate(
      tiers,
      headcount,
      locationKind,
      offSiteDistance,
    );
    return sum + hours * rate;
  }, 0);

  const { discountAmount, finalPrice } = applySellDiscount(
    originalPrice,
    discountType,
    discountValue,
  );

  return {
    ratePerHour: resolvePrivateSharedSessionRate(tiers, headcount, "studio", null),
    tier,
    extraPeople,
    topTier,
    originalPrice,
    discountAmount,
    finalPrice,
  };
}

export function computePrivateSharedSellPriceFromHourSplit(
  tiers: PrivateSharedTier[],
  headcount: number,
  hourSplit: PrivateSharedHourSplit,
  discountType?: "percentage" | "fixed",
  discountValue?: number,
) {
  const { studioHours, nearHours, farHours } = hourSplit;
  const purchasedHours = studioHours + nearHours + farHours;
  const { tier, extraPeople, topTier } = resolvePrivateSharedTierRate(
    tiers,
    headcount,
  );

  const rateStudio = resolvePrivateSharedSessionRate(
    tiers,
    headcount,
    "studio",
    null,
  );
  const rateNear = resolvePrivateSharedSessionRate(
    tiers,
    headcount,
    "off_site",
    "near",
  );
  const rateFar = resolvePrivateSharedSessionRate(
    tiers,
    headcount,
    "off_site",
    "far",
  );

  const originalPrice =
    studioHours * rateStudio + nearHours * rateNear + farHours * rateFar;

  const { discountAmount, finalPrice } = applySellDiscount(
    originalPrice,
    discountType,
    discountValue,
  );

  return {
    ratePerHour: rateStudio,
    tier,
    extraPeople,
    topTier,
    originalPrice,
    discountAmount,
    finalPrice,
    purchasedHours,
    rateStudio,
    rateNear,
    rateFar,
  };
}

/** Backward compatible — uses studio tier rate × purchased hours */
export function computePrivateSharedSellPrice(
  tiers: PrivateSharedTier[],
  headcount: number,
  purchasedHours: number,
  discountType?: "percentage" | "fixed",
  discountValue?: number,
) {
  const { ratePerHour, tier, extraPeople, topTier } =
    resolvePrivateSharedTierRate(tiers, headcount);
  const originalPrice = computePrivateSharedOriginalPrice(
    ratePerHour,
    purchasedHours,
  );
  const { discountAmount, finalPrice } = applySellDiscount(
    originalPrice,
    discountType,
    discountValue,
  );
  return {
    ratePerHour,
    tier,
    extraPeople,
    topTier,
    originalPrice,
    discountAmount,
    finalPrice,
  };
}
